import React from "react";
import { listedIn } from "../../config/catalog";
import type { CatalogId } from "../../config/catalog";
import type { DataProductId } from "../../config/products";
import { useCoverage } from "../../hooks/useCoverage";
import { useProductEntitlements } from "../../hooks/useProductEntitlements";
import { useReadOnly } from "../../hooks/useReadOnly";
import CountryCoverageRow from "../data/CountryCoverageRow";
import DeliveryLegend from "../data/DeliveryLegend";
import Spinner from "../common/Spinner";

const LIST = "mt-3 divide-y divide-paper-300 rounded-2xl border border-paper-300 bg-surface";

/** The data products Status covers, each with its availability. Real Time has no availability API, so it is left out. */
const STATUS_PRODUCTS: Partial<Record<CatalogId, DataProductId>> = {
  activities: "activities",
  odm: "odm",
};

/**
 * Status: which data products have data, per country, one bar per day, from the real availability.
 */
const StatusSection: React.FC = () => {
  const { loading: entitlementsLoading, isEntryLocked } = useProductEntitlements();
  const { rows, retry, loading } = useCoverage();
  const { supportView } = useReadOnly();

  const blocks = listedIn("data").filter((e) => STATUS_PRODUCTS[e.id] !== undefined && !isEntryLocked(e.id));

  return (
    <div className="w-full">
      <header className="mb-10 space-y-1">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">Workbench</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">Status</h1>
        <p className="text-lg text-ink-muted max-w-xl">
          Which data products have data, per country. Each bar is one day (UTC).
        </p>
        {supportView ? (
          <p className="text-sm text-ink-muted">{`Showing data for ${supportView.name} (support view).`}</p>
        ) : null}
        <DeliveryLegend className="pt-2" />
      </header>

      <div role="status">{loading ? <span className="sr-only">Checking deliveries</span> : null}</div>

      {entitlementsLoading ? (
        <div className="py-16">
          <Spinner />
        </div>
      ) : blocks.length === 0 ? (
        <p className="text-ink-muted">Your plan doesn't include any data products yet.</p>
      ) : (
        <div className="space-y-8">
          {blocks.map((b) => {
            const headingId = `status-${b.id}`;
            const product = STATUS_PRODUCTS[b.id]!;
            return (
              <section key={b.id} aria-labelledby={headingId}>
                <h2 id={headingId} className="font-display text-xl font-bold text-ink">
                  {b.title}
                </h2>
                <ul className={LIST}>
                  {rows[product].map((row) => (
                    <CountryCoverageRow
                      key={row.country}
                      country={row.country}
                      product={product}
                      status={row.status}
                      onRetry={() => retry(row.country)}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusSection;
